import * as THREE from "three";
import Orchestrator from "../Orchestrator.js";
import PointerWave, { MAX_TRAIL } from "./PointerWave.js";

/** GPU adapter for the bounded pointer/idle trail.
 * RGBA texels: world X, world Z, age in seconds, signed strength.
 * Negative strength marks pointer samples; positive marks automatic ripples.
 */
export default class MouseTrail extends PointerWave {
    constructor(bounds, surfaceHeight = 0) {
        super(bounds);
        this.orchestrator = new Orchestrator();
        this.camera = this.orchestrator.camera.instance;
        this.canvas = this.orchestrator.canvas;
        this.mouseCoords = new THREE.Vector2();
        this.raycaster = new THREE.Raycaster();
        this.rayPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(bounds, bounds),
            new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, visible: false }),
        );
        this.rayPlane.rotation.x = -Math.PI / 2;
        this.rayPlane.position.y = surfaceHeight;
        this.rayPlane.updateMatrixWorld(true);

        this.trailData = new Float32Array(MAX_TRAIL * 4);
        this.trailTexture = new THREE.DataTexture(
            this.trailData, MAX_TRAIL, 1, THREE.RGBAFormat, THREE.FloatType,
        );
        this.trailTexture.needsUpdate = true;
        this._uniforms = {
            uTrailTexture: { value: this.trailTexture },
            uTrailCount: { value: 0 },
            uFadeTime: { value: this.params.fadeTime },
        };
        this.rect = this.canvas.getBoundingClientRect();
        this.orchestrator.sizes.emitter.on("resize", () => {
            this.rect = this.canvas.getBoundingClientRect();
        });
        this.bindPointerEvents();
    }

    get uniforms() {
        return this._uniforms;
    }

    update(delta) {
        super.update(delta);
        const count = this.trail.length;
        if (count > 0 || this._uniforms.uTrailCount.value > 0) {
            for (let i = 0; i < count; i++) {
                const ti = i * 4;
                this.trailData[ti] = this.trail[i].x;
                this.trailData[ti + 1] = this.trail[i].z;
                this.trailData[ti + 2] = this.trail[i].age;
                this.trailData[ti + 3] = this.trail[i].strength;
            }
            this.trailTexture.needsUpdate = true;
            this._uniforms.uTrailCount.value = count;
        }
    }

    bindPointerEvents() {
        this.onPointerMove = (e) => {
            this.mouseCoords.set(
                ((e.clientX - this.rect.left) / this.rect.width) * 2 - 1,
                -((e.clientY - this.rect.top) / this.rect.height) * 2 + 1,
            );
            this.raycaster.setFromCamera(this.mouseCoords, this.camera);
            const hits = this.raycaster.intersectObject(this.rayPlane);
            if (hits.length === 0) return;
            const { x, z } = hits[0].point;
            this.recordPointer(x, z);
        };
        this.onPointerLeave = () => this.leave();
        this.canvas.addEventListener("pointerdown", this.onPointerMove);
        this.canvas.addEventListener("pointermove", this.onPointerMove);
        this.canvas.addEventListener("pointerleave", this.onPointerLeave);
    }

    dispose() {
        this.canvas.removeEventListener("pointerdown", this.onPointerMove);
        this.canvas.removeEventListener("pointermove", this.onPointerMove);
        this.canvas.removeEventListener("pointerleave", this.onPointerLeave);
        this.trailTexture.dispose();
    }
}
